import React, { useMemo } from "react";
import { Card, Col, Divider, Grid, Row, Typography } from "antd";

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

type Block =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "note"; text: string };

type Section = {
  id: string;
  title: string;
  blocks: Block[];
};

const TermsServiceView = ({
  contactEmail = "support@platformb2b.com",
  hotline = "1900-xxxx",
  lastUpdated = "22/08/2025",
  platformName = "AI Procurement",
  companyOwner = "Apetechs",
}: {
  contactEmail?: string;
  hotline?: string;
  lastUpdated?: string;
  platformName?: string;
  companyOwner?: string;
}) => {
  const screens = useBreakpoint();

  const sections: Section[] = useMemo(
    () => [
      {
        id: "gioi-thieu",
        title: "1. Giới thiệu",
        blocks: [
          {
            type: "paragraph",
            text: `Chào mừng bạn đến với nền tảng AI Procurement. Khi sử dụng dịch vụ của chúng tôi, bạn đồng ý tuân thủ các Điều khoản Dịch vụ này. Vui lòng đọc kỹ trước khi tiếp tục.`,
          },
        ],
      },
      {
        id: "thong-tin-dang-ky",
        title: "2. Phạm vi dịch vụ",
        blocks: [
          {
            type: "paragraph",
            text: "Nền tảng AI Procurement cung cấp các dịch vụ bao gồm nhưng không giới hạn:",
          },
          {
            type: "list",
            items: [
              "Quản lý yêu cầu báo giá (RFQ), báo giá.",
              "Gợi ý nhà cung cấp và tối ưu chi phí bằng AI.",
              "Quản trị giao dịch, dữ liệu.",
            ],
          },
        ],
      },
      {
        id: "bao-mat-tai-khoan",
        title: "3. Tài khoản và bảo mật",
        blocks: [
          {
            type: "list",
            items: [
              "Người dùng phải cung cấp thông tin chính xác khi đăng ký tài khoản.",
              "Bạn chịu trách nhiệm giữ bí mật thông tin đăng nhập.",
              "Mọi hành vi phát sinh từ tài khoản sẽ được coi là do chính bạn thực hiện.",
            ],
          },
        ],
      },
      {
        id: "quyen-nghia-vu-nguoi-dung",
        title: "4. Quyền và nghĩa vụ của người dùng",
        blocks: [
          { type: "paragraph", text: "Người dùng cam kết:" },
          {
            type: "list",
            items: [
              "Không sử dụng dịch vụ vào mục đích gian lận, vi phạm pháp luật.",
              "Không can thiệp trái phép vào hệ thống hoặc dữ liệu của người khác",
              " Tuân thủ chính sách bảo mật và chính sách cookie của chúng tôi.",
            ],
          },
        ],
      },
      {
        id: "quyen-nghia-vu-cua-chung-toi",
        title: "5. Quyền và nghĩa vụ của chúng tôi",
        blocks: [
          { type: "paragraph", text: "Chúng tôi có quyền:" },
          {
            type: "list",
            items: [
              "Tạm ngưng hoặc chấm dứt tài khoản nếu phát hiện vi phạm.",
              "Nâng cấp, thay đổi, gián đoạn dịch vụ để bảo trì hoặc cải tiến.",
              "Thu thập và xử lý dữ liệu nhằm nâng cao chất lượng dịch vụ, theo Chính sách Bảo mật.",
            ],
          },
        ],
      },
      {
        id: "cham-dut-tai-khoan",
        title: "6. Thanh toán và phí dịch vụ",
        blocks: [
          {
            type: "list",
            items: [
              "Một số tính năng có thể yêu cầu phí dịch vụ hoặc gói đăng ký.",
              "Người dùng có trách nhiệm thanh toán đầy đủ, đúng hạn.",
              "Phí đã thanh toán thường không hoàn lại, trừ khi có thỏa thuận khác.",
            ],
          },
        ],
      },
      {
        id: "thay-doi-dieu-khoan",
        title: "7. Giới hạn trách nhiệm",
        blocks: [
          {
            type: "list",
            items: [
              "Chúng tôi không chịu trách nhiệm cho thiệt hại gián tiếp, mất lợi nhuận hoặc dữ liệu do việc sử dụng dịch vụ.",
              "Dữ liệu và khuyến nghị từ AI chỉ mang tính tham khảo, người dùng tự chịu trách nhiệm với các quyết định mua sắm.",
            ],
          },
        ],
      },
      {
        id: "lien-he",
        title: "8. Sở hữu trí tuệ",
        blocks: [
          {
            type: "paragraph",
            text: "Mọi thắc mắc liên quan đến việc đăng ký và sử dụng tài khoản, vui lòng liên hệ:",
          },
          {
            type: "list",
            items: [
              `Toàn bộ mã nguồn, thuật toán AI, giao diện, logo thuộc sở hữu của Công ty Cổ phần Apetechs.`,
              `Người dùng không được sao chép, chỉnh sửa, phân phối lại nếu chưa có sự đồng ý bằng văn bản.`,
            ],
          },
        ],
      },
      {
        id: "luat-ap-dung",
        title: "9. Luật áp dụng và giải quyết tranh chấp",
        blocks: [
          {
            type: "list",
            items: [
              `Điều khoản này được điều chỉnh theo pháp luật Việt Nam (hoặc pháp luật quốc tế tuỳ thị trường mục tiêu).`,
              `Tranh chấp sẽ được giải quyết thông qua thương lượng. Nếu không đạt kết quả, sẽ đưa ra trọng tài hoặc toà án có thẩm quyền`,
            ],
          },
        ],
      },
      {
        id: "thay-doi-dieu-khoan",
        title: "10. Thay đổi điều khoản",
        blocks: [
          {
            type: "paragraph",
            text: "Chúng tôi có thể cập nhật Điều khoản Dịch vụ. Phiên bản mới sẽ được thông báo trên nền tảng kèm ngày hiệu lực.",
          },
        ],
      },
    ],
    [platformName, companyOwner, contactEmail, hotline]
  );

  return (
    <div className="pp-container">
      <Row gutter={16} className="pp-grid">
        <Col xs={24} lg={24}>
          <Title level={1} className="pp-title">
            ĐIỀU KHOẢN DỊCH VỤ
          </Title>
          <Text className="pp-subtle">
            Nền tảng: {platformName} • Cập nhật: {lastUpdated}
          </Text>

          <Card size="small" className="pp-main-card">
            {sections.map((s, idx) => (
              <div key={s.id}>
                <section id={s.id} className="pp-section">
                  <Title level={3} className="pp-section-title">
                    {s.title}
                  </Title>

                  {s.blocks.map((b, i) => {
                    if (b.type === "paragraph") {
                      return (
                        <Paragraph key={i} className="pp-block">
                          {b.text}
                        </Paragraph>
                      );
                    }
                    if (b.type === "list") {
                      return (
                        <ul key={i} className="pp-list pp-block">
                          {b.items.map((it, j) => (
                            <li key={j}>{it}</li>
                          ))}
                        </ul>
                      );
                    }
                    if (b.type === "note") {
                      return (
                        <div key={i} className="pp-note pp-block">
                          {b.text}
                        </div>
                      );
                    }
                    return null;
                  })}
                </section>

                {idx < sections.length - 1 && (
                  <Divider className="pp-divider" />
                )}
              </div>
            ))}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TermsServiceView;
